import { useCallback, useEffect, useRef, useState } from 'react';
import { AppWindow, Bookmark, Check, Download, Eye, GraduationCap, Pencil, Play, Trash2, Users, Video } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useI18n } from '../../lib/i18n';
import { fmtElapsed } from '../../lib/share/peer';
import { emitRecordingChange, onRecordingChange } from '../../lib/rec/store';
import { TERMINAL_STATUSES, type RecordingView } from '../../lib/rec/types';
import { RecordingPlayer } from './RecordingPlayer';
import { useRec } from './RecProvider';
import { SendToTrainingPanel } from './SendToTraining';

/**
 * LA TARJETA de una grabación (spec §4.12.5): poster 16:9, título, duración, dueño, fecha, estado
 * (Processing… / Ready / Failed), Play inline, "Open in a window", Download, Keep, Delete; para el
 * dueño "Sent to 3 · Seen by 2 · 1 watched to the end". Se pinta por FK (`recording_id`) en
 * Messaging, InspectorMessages y ClaimChat: la tarjeta lee `GET /:id` sola (lazy).
 *
 * Repollea `GET /:id` cada 5 s SOLO mientras está visible en pantalla (IntersectionObserver +
 * pestaña visible) y no está en un estado terminal. El socket (`recording.ready`) y el propio
 * flujo de grabación la despiertan por `onRecordingChange`.
 */
const POLL_MS = 5_000;

export function fmtDate(iso: string, lang: string): string {
  try { return new Date(iso).toLocaleString(lang === 'es' ? 'es' : 'en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return iso; }
}

export function RecordingCard({ id, compact, autoOpen, allowRename }: { id: number; compact?: boolean; autoOpen?: boolean; allowRename?: boolean }) {
  const { t, lang } = useI18n();
  const rec = useRec();
  const [data, setData] = useState<RecordingView | null>(null);
  const [err, setErr] = useState<'noAccess' | 'gone' | 'error' | null>(null);
  const [playing, setPlaying] = useState(!!autoOpen);
  const [visible, setVisible] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [training, setTraining] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const busyRef = useRef(false);

  const load = useCallback(async (): Promise<RecordingView | null> => {
    if (busyRef.current) return null;
    busyRef.current = true;
    try {
      const r = await api.get<RecordingView>(`/api/recordings/${id}`);
      setData(r);
      setErr(r.recording.status === 'deleted' ? 'gone' : null);
      return r;
    } catch (e) {
      const st = e instanceof ApiError ? e.status : 0;
      setErr(st === 403 ? 'noAccess' : st === 404 || st === 410 ? 'gone' : 'error');
      return null;
    } finally { busyRef.current = false; }
  }, [id]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => onRecordingChange(id, (r) => {
    if (r) setData((d) => (d ? { ...d, recording: r } : d));
    void load();
  }), [id, load]);

  // Visible en pantalla → se puede repollear.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const io = new IntersectionObserver((es) => setVisible(es.some((e) => e.isIntersecting)), { threshold: 0.1 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const status = data?.recording.status;
  const polling = visible && !!status && !TERMINAL_STATUSES.has(status) && !err;
  useEffect(() => {
    if (!polling) return undefined;
    const tick = () => { if (!document.hidden) void load(); };
    const iv = window.setInterval(tick, POLL_MS);
    return () => clearInterval(iv);
  }, [load, polling]);

  const refreshUrl = useCallback(async () => (await load())?.mediaUrl ?? null, [load]);

  if (err) {
    return (
      <div ref={rootRef} className={`rec-card is-err${compact ? ' compact' : ''}`}>
        <Video size={16} strokeWidth={1.75} aria-hidden />
        <span>{err === 'noAccess' ? t('rec.noAccess') : err === 'gone' ? t('rec.deleted') : t('rec.error')}</span>
      </div>
    );
  }
  if (!data) return <div ref={rootRef} className={`rec-card is-loading${compact ? ' compact' : ''}`}><Video size={16} strokeWidth={1.75} aria-hidden /><span>{t('common.loading')}</span></div>;

  const r = data.recording;
  const ready = r.status === 'ready';
  const failed = r.status === 'failed';
  const canPlay = !!data.mediaUrl && (ready || failed);
  const dur = r.durationMs != null ? fmtElapsed(r.durationMs) : null;

  const keep = async () => {
    try { await api.post(`/api/recordings/${id}/keep`, {}); rec.notify({ kind: 'kept' }); emitRecordingChange(id, null); }
    catch { rec.notify({ kind: 'error' }); }
  };
  const del = async () => {
    if (!window.confirm(t('rec.deleteConfirm'))) return;
    try { await api.post(`/api/recordings/${id}/delete`, {}); rec.notify({ kind: 'deleted' }); emitRecordingChange(id, null); }
    catch (e) {
      // Una lección de Training la usa (mig 0472): se dice por qué en vez de un error mudo.
      if (e instanceof ApiError && e.code === 'recording_in_training') window.alert(t('trn.recInTraining'));
      else rec.notify({ kind: 'error' });
    }
  };
  const openWindow = () => window.open(`/rec/${id}`, '_blank', 'noopener,popup,width=1000,height=640');
  // Reproducir = visor EMBEBIDO en el centro (pedido del dueño). Sin provider (caso defensivo) cae al player inline.
  const open = () => { if (!canPlay) return; if (rec.viewerAvailable) rec.openViewer(id); else setPlaying(true); };
  const rename = async () => {
    const clean = editTitle.trim();
    setEditing(false);
    if (!clean || clean === r.title) return;
    try {
      await api.patch(`/api/recordings/${id}`, { title: clean });
      setData((d) => (d ? { ...d, recording: { ...d.recording, title: clean } } : d));
      emitRecordingChange(id, null);
    } catch { rec.notify({ kind: 'error' }); }
  };

  return (
    <div ref={rootRef} className={`rec-card${compact ? ' compact' : ''}`}>
      {playing && canPlay ? (
        <RecordingPlayer id={id} mediaUrl={data.mediaUrl} posterUrl={data.posterUrl} title={r.title} onExpired={refreshUrl} autoPlay />
      ) : (
        <button type="button" className="rec-poster" onClick={open} disabled={!canPlay} aria-label={canPlay ? t('rec.play') : r.status === 'failed' ? t('rec.statusFailed') : t('rec.processing')}>
          {data.posterUrl ? <img src={data.posterUrl} alt="" /> : <span className="rec-poster-empty"><Video size={28} strokeWidth={1.5} aria-hidden /></span>}
          {canPlay
            ? <span className="rec-play"><Play size={22} strokeWidth={2.2} fill="currentColor" /></span>
            : <span className={`rec-state${failed ? ' failed' : ''}`}>{failed ? t('rec.statusFailed') : r.status === 'uploading' ? t('rec.statusUploading') : t('rec.processing')}</span>}
          {dur && <span className="rec-dur ss-mono">{dur}</span>}
        </button>
      )}
      <div className="rec-card-body">
        {allowRename && data.isOwner && editing ? (
          <div className="rec-mine-edit">
            <input className="input sm" value={editTitle} autoFocus maxLength={140} onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void rename(); if (e.key === 'Escape') setEditing(false); }} />
            <button type="button" className="btn ghost sm" onClick={() => { void rename(); }} aria-label={t('common.save')}><Check size={13} strokeWidth={2} /></button>
          </div>
        ) : (
          <div className="rec-card-title" title={r.title}>
            {r.title}
            {allowRename && data.isOwner && <button type="button" className="btn ghost sm rec-mine-pencil" onClick={() => { setEditTitle(r.title); setEditing(true); }} title={t('rec.rename')} aria-label={t('rec.rename')}><Pencil size={12} strokeWidth={1.75} /></button>}
          </div>
        )}
        <div className="rec-card-meta">
          <span>{r.owner.name}</span>
          <span>·</span>
          <span>{fmtDate(r.createdAt, lang)}</span>
          {/* "Processing…" ya está centrado en el poster (`rec-state`): acá solo lo que el poster no dice. */}
          {ready && !r.transcoded && <span className="rec-chip warn" title={t('rec.failedRemux', { title: r.title })}>{t('rec.noSeek')}</span>}
          {failed && <span className="rec-chip failed">{t('rec.statusFailed')}</span>}
          {r.expiresAt && !r.keepAt && r.claimId == null && <span className="rec-chip soft" title={t('rec.expires', { date: fmtDate(r.expiresAt, lang) })}>{t('rec.expires', { date: fmtDate(r.expiresAt, lang) })}</span>}
        </div>
        {data.isOwner && (
          <div className="rec-card-stats">
            <span><Users size={12} strokeWidth={2} aria-hidden /> {t('rec.sentTo', { n: r.sentTo })}</span>
            <span><Eye size={12} strokeWidth={2} aria-hidden /> {t('rec.seenBy', { n: r.seenBy })}</span>
            <span>{t('rec.watchedToEnd', { n: r.watchedToEnd })}</span>
          </div>
        )}
        <div className="rec-card-actions">
          {canPlay && !playing && <button type="button" className="btn ghost sm" onClick={open}><Play size={13} strokeWidth={2} /> {t('rec.play')}</button>}
          <button type="button" className="btn ghost sm" onClick={openWindow} title={t('rec.openWindow')}><AppWindow size={13} strokeWidth={1.75} /> {t('rec.openWindow')}</button>
          {canPlay && <a className="btn ghost sm" href={`/api/recordings/${id}/media`} target="_blank" rel="noreferrer" title={t('rec.download')}><Download size={13} strokeWidth={1.75} /> {t('rec.download')}</a>}
          {data.isOwner && r.claimId == null && !r.keepAt && ready && <button type="button" className="btn ghost sm" onClick={() => { void keep(); }} title={t('rec.keepHint')}><Bookmark size={13} strokeWidth={1.75} /> {t('rec.keep')}</button>}
          {allowRename && data.isOwner && r.status !== 'uploading' && <button type="button" className={`btn ghost sm${training ? ' on' : ''}`} onClick={() => setTraining((v) => !v)}><GraduationCap size={13} strokeWidth={1.75} /> {t('trn.sendToTraining')}</button>}
          {data.canDelete && <button type="button" className="btn ghost sm rec-del" onClick={() => { void del(); }}><Trash2 size={13} strokeWidth={1.75} /> {t('rec.delete')}</button>}
        </div>
        {training && <SendToTrainingPanel recordingId={id} onDone={() => setTraining(false)} onSent={() => emitRecordingChange(id, null)} />}
      </div>
    </div>
  );
}
